import type React from 'react';
import { useTranslation } from 'react-i18next';
import { MigrationCtaButton } from './MigrationCtaButton';
import { MigrationHelperText } from './MigrationHelperText';

interface MigrationActionProps {
  isAdmin: boolean;
  /** i18n key naming why the API would refuse; when set, no action is offered. */
  blockedReasonKey: string | null;
  isStarting: boolean;
  onAct: () => void;
  /** i18n key of the button label. */
  actionKey: string;
  /** i18n key of the sentence a viewer reads instead of the button. */
  viewerHelperKey: string;
}

/**
 * The action half of every state that asks something of the merchant: a button
 * for an admin, the reason the API would refuse, or the sentence naming who can
 * act.
 *
 * Written once because three states render it identically, and because the
 * ordering matters: a state that reaches this component always offers a way
 * forward or says why there is none. Rebuilding it per state is how `blocked`
 * ended up with neither.
 */
export const MigrationAction: React.FC<MigrationActionProps> = ({
  isAdmin,
  blockedReasonKey,
  isStarting,
  onAct,
  actionKey,
  viewerHelperKey,
}) => {
  const { t } = useTranslation();

  if (!isAdmin) {
    return <MigrationHelperText>{t(viewerHelperKey)}</MigrationHelperText>;
  }

  if (blockedReasonKey) {
    return <MigrationHelperText>{t(blockedReasonKey)}</MigrationHelperText>;
  }

  return <MigrationCtaButton label={t(actionKey)} onClick={onAct} isLoading={isStarting} />;
};
